/**
 * The pediatrician report: one baby's last seven full days as the numbers a
 * checkup asks about. Ounces and bottles a day and how far apart, wet and
 * dirty diapers, sleep by night (total, night against day, the longest
 * stretch), every medicine dose, and the notes. Built on the overview's
 * derivations and the phone's local days; pure, so every rule is unit-tested.
 *
 * It reports what was logged and judges nothing. Averages cover full days
 * only: today so far and the day logging began are shown but left out, and
 * each kind is averaged over the days it was logged, so a day with no bottle
 * logged never reads as a day without feeds.
 */
import { MAX_PLAUSIBLE_SLEEP_MS, type Tracked } from "@babia/checks";
import { type EntryLike, formatDose, type NoteKind } from "./entry-text.ts";
import { dailyTotals, type SleepRow, type SleepSegment, sleepRows } from "./overview.ts";
import { addLocalDays, isSameLocalDay, median, startOfLocalDay } from "./time.ts";

/** Full days the report covers, ending at midnight last night. */
export const REPORT_DAYS = 7;

/** What the report covers, leaving out what the family doesn't track. */
export function reportContents(tracking: Tracked): string {
  const kinds = [
    "bottles",
    ...(tracking.diaper ? ["diapers"] : []),
    ...(tracking.sleep ? ["sleep"] : []),
    "medicine",
  ];
  return `${kinds.join(", ")}, and notes`;
}
/** Night, for the night/day split: 7 pm to 7 am by the wall clock. */
export const NIGHT_FROM_HOUR = 19;
export const NIGHT_TO_HOUR = 7;

const HOUR = 3_600_000;
const endOf = (e: EntryLike, now: Date) => (e.endedAt ?? now).getTime();
const overlap = (a0: number, a1: number, b0: number, b1: number) =>
  Math.max(0, Math.min(a1, b1) - Math.max(a0, b0));
const byTime = (a: EntryLike, b: EntryLike) => a.at.getTime() - b.at.getTime();
const sum = (xs: readonly number[]) => xs.reduce((n, x) => n + x, 0);
const mean = (xs: readonly number[]) => (xs.length ? sum(xs) / xs.length : null);

export interface ReportPeriod {
  /** Local midnight of the first day shown. */
  from: Date;
  /** Local midnight today: full days end here, today so far comes after. */
  to: Date;
  /** Full days in the period (fewer than seven for a new household). */
  days: number;
  /** The household's first entry, when logging began inside the period. */
  loggingBegan: Date | null;
}

/**
 * The last seven full days, or fewer if the household began logging since.
 * Shared by every baby so twins' pages cover the same days.
 */
export function reportPeriod(entries: readonly EntryLike[], now: Date): ReportPeriod {
  const to = startOfLocalDay(now);
  let first: Date | null = null;
  for (const e of entries) if (!first || e.at < first) first = e.at;
  if (!first) return { from: to, to, days: 0, loggingBegan: null };
  const earliest = addLocalDays(now, -REPORT_DAYS);
  const loggingBegan = first >= earliest ? first : null;
  const from = loggingBegan ? (first < to ? startOfLocalDay(first) : to) : earliest;
  const days = Math.round((to.getTime() - from.getTime()) / (24 * HOUR));
  return { from, to, days, loggingBegan };
}

export interface ReportDay {
  /** Local midnight. */
  day: Date;
  isToday: boolean;
  /** Anything at all logged for this baby that day. */
  logged: boolean;
  /** Logged and a full day (not today, not the day logging began): in the averages. */
  counted: boolean;
  oz: number;
  /** None means no bottle was logged: shown as a dash and left out of that average. */
  bottles: number;
  /** Likewise for diapers. */
  changes: number;
  wet: number;
  dirty: number;
  /** The longest wait between bottles that began this day. */
  longestGapMs: number | null;
}

export interface ReportNight {
  /** Local noon the row starts at: "the night of" this date. */
  start: Date;
  segments: SleepSegment[];
  longest: SleepRow["longest"];
  totalMs: number;
  /** Asleep between 7 pm and 7 am. */
  nightMs: number;
  /** Asleep the rest of the row: the afternoon before and the morning after. */
  dayMs: number;
  /** Still going (before noon, last night's row is): shown so far. */
  inProgress: boolean;
  /** Sleep logged across the whole row: in the averages. */
  counted: boolean;
}

export interface Range {
  avg: number;
  min: number;
  max: number;
}

export interface PediatricReport {
  /** The period's days, oldest first, then today so far. */
  days: ReportDay[];
  /** Noon-to-noon rows for the period's nights, oldest first. */
  nights: ReportNight[];
  /** Full days with anything logged. */
  countedDays: number;
  /** Null when no bottle was logged on a counted day. */
  feeding: {
    /** Full days with a bottle logged: what these averages divide by. */
    days: number;
    oz: Range;
    bottlesPerDay: number;
    ozPerBottle: number;
    /** The median wait between bottles. */
    typicalGapMs: number | null;
    /** The average of each day's longest wait. */
    longestGapMs: number | null;
    /** Formula names, most bottles first. */
    formulas: { label: string; bottles: number }[];
  } | null;
  /** Null when no change was logged on a counted day; over the days one was. */
  diapers: { days: number; wet: number; dirty: number } | null;
  /** Null until a whole night's sleep is logged. */
  sleep: {
    nights: number;
    totalMs: number;
    nightMs: number;
    dayMs: number;
    longest: { avgMs: number; best: NonNullable<SleepRow["longest"]> } | null;
  } | null;
  /**
   * Every dose from the period's start through now, by first given. `dose` is
   * its amount ("2.5 ml", null when none was logged); `each` is that amount
   * when two or more doses all had the same one.
   */
  meds: {
    label: string;
    doses: { at: Date; dose: string | null }[];
    days: number;
    each: string | null;
  }[];
  /**
   * Every note from the period's start through now, oldest first; a bath's
   * text may be "", and so may a weight's with its number (`weightGrams`).
   */
  notes: { id: string; at: Date; kind: NoteKind; text: string; weightGrams: number | null }[];
  /** Sleeps too long to be real (a missed Wake up tap), left out of sleep. */
  leftOut: EntryLike[];
}

/** One baby's report over a period from `reportPeriod`. */
export function pediatricReport(
  entries: readonly EntryLike[],
  babyId: string,
  now: Date,
  period: ReportPeriod,
): PediatricReport {
  const { from, loggingBegan } = period;
  const mine = entries.filter((e) => e.babyId === babyId);
  const tooLong = (e: EntryLike) =>
    e.type === "sleep" && endOf(e, now) - e.at.getTime() > MAX_PLAUSIBLE_SLEEP_MS;
  const kept = mine.filter((e) => !tooLong(e));
  const leftOut = mine.filter((e) => tooLong(e) && endOf(e, now) > from.getTime()).sort(byTime);

  // Waits between bottles, each belonging to the day it began. One holding a
  // whole day without a bottle is a hole in the log, not a wait.
  const feeds = kept.filter((e) => e.type === "feed").sort(byTime);
  const gaps: { at: Date; ms: number }[] = [];
  for (let i = 1; i < feeds.length; i++) {
    const a = feeds[i - 1]!.at;
    const b = feeds[i]!.at;
    if (startOfLocalDay(b) > addLocalDays(a, 1)) continue;
    gaps.push({ at: a, ms: b.getTime() - a.getTime() });
  }

  const days: ReportDay[] = dailyTotals(kept, babyId, now, from).map((t) => {
    const d0 = t.day.getTime();
    const d1 = addLocalDays(t.day, 1).getTime();
    // Every entry shows the day was logged, a sleep too long to count included.
    const logged = mine.some((e) => {
      const at = e.at.getTime();
      if (at >= d0 && at < d1) return true;
      return e.type === "sleep" && at < d1 && endOf(e, now) > d0;
    });
    const began = loggingBegan !== null && isSameLocalDay(loggingBegan, t.day);
    const ofDay = gaps.filter((g) => g.at.getTime() >= d0 && g.at.getTime() < d1);
    return {
      day: t.day,
      isToday: t.isToday,
      logged,
      counted: logged && !t.isToday && !began,
      oz: t.oz,
      bottles: t.bottles,
      changes: t.changes,
      wet: t.wet,
      dirty: t.dirty,
      longestGapMs: ofDay.length ? Math.max(...ofDay.map((g) => g.ms)) : null,
    };
  });
  const counted = days.filter((d) => d.counted);
  const countedDay = (at: Date) => counted.some((d) => isSameLocalDay(d.day, at));

  const fed = counted.filter((d) => d.bottles > 0);
  const bottles = sum(fed.map((d) => d.bottles));
  const ozTotal = sum(fed.map((d) => d.oz));
  const formulas = new Map<string, number>();
  for (const e of feeds)
    if (e.label && countedDay(e.at)) formulas.set(e.label, (formulas.get(e.label) ?? 0) + 1);
  const gapsCounted = gaps.filter((g) => countedDay(g.at)).map((g) => g.ms);
  const feeding = fed.length
    ? {
        days: fed.length,
        oz: {
          avg: ozTotal / fed.length,
          min: Math.min(...fed.map((d) => d.oz)),
          max: Math.max(...fed.map((d) => d.oz)),
        },
        bottlesPerDay: bottles / fed.length,
        ozPerBottle: ozTotal / bottles,
        typicalGapMs: median(gapsCounted),
        longestGapMs: mean(fed.flatMap((d) => (d.longestGapMs === null ? [] : [d.longestGapMs]))),
        formulas: [...formulas]
          .map(([label, n]) => ({ label, bottles: n }))
          .sort((a, b) => b.bottles - a.bottles || a.label.localeCompare(b.label)),
      }
    : null;

  const changed = counted.filter((d) => d.changes > 0);
  const diapers = changed.length
    ? {
        days: changed.length,
        wet: sum(changed.map((d) => d.wet)) / changed.length,
        dirty: sum(changed.map((d) => d.dirty)) / changed.length,
      }
    : null;

  const sleeps = kept.filter((e) => e.type === "sleep");
  const nights: ReportNight[] = sleepRows(kept, babyId, now, from)
    .filter((r) => r.start >= from)
    .reverse()
    .map((r) => {
      const y = r.start.getFullYear();
      const m = r.start.getMonth();
      const d = r.start.getDate();
      const n0 = new Date(y, m, d, NIGHT_FROM_HOUR).getTime();
      const n1 = new Date(y, m, d + 1, NIGHT_TO_HOUR).getTime();
      const nightMs = sum(
        sleeps.map((e) => overlap(e.at.getTime(), Math.min(endOf(e, now), now.getTime()), n0, n1)),
      );
      const inProgress = r.nowAt !== null;
      return {
        start: r.start,
        segments: r.segments,
        longest: r.longest,
        totalMs: r.totalMs,
        nightMs,
        dayMs: r.totalMs - nightMs,
        inProgress,
        counted:
          r.segments.length > 0 &&
          !inProgress &&
          (loggingBegan === null || r.start >= loggingBegan),
      };
    });
  const full = nights.filter((n) => n.counted);
  const stretches = full.flatMap((n) => (n.longest ? [n.longest] : []));
  const sleep = full.length
    ? {
        nights: full.length,
        totalMs: mean(full.map((n) => n.totalMs))!,
        nightMs: mean(full.map((n) => n.nightMs))!,
        dayMs: mean(full.map((n) => n.dayMs))!,
        longest: stretches.length
          ? {
              avgMs: mean(stretches.map((s) => s.ms))!,
              best: stretches.reduce((a, b) => (b.ms > a.ms ? b : a)),
            }
          : null,
      }
    : null;

  const meds = new Map<string, { at: Date; dose: string | null }[]>();
  for (const e of mine.filter((e) => e.type === "meds" && e.label && e.at >= from).sort(byTime)) {
    const dose = e.doseAmount !== null && e.doseUnit ? formatDose(e.doseAmount, e.doseUnit) : null;
    meds.set(e.label!, [...(meds.get(e.label!) ?? []), { at: e.at, dose }]);
  }

  return {
    days,
    nights,
    countedDays: counted.length,
    feeding,
    diapers,
    sleep,
    meds: [...meds].map(([label, doses]) => ({
      label,
      doses,
      days: new Set(doses.map((d) => startOfLocalDay(d.at).getTime())).size,
      each:
        doses.length > 1 && doses.every((d) => d.dose !== null && d.dose === doses[0]!.dose)
          ? doses[0]!.dose
          : null,
    })),
    notes: mine
      .filter((e) => e.type === "note" && e.at >= from)
      .sort(byTime)
      .map((e) => ({
        id: e.id,
        at: e.at,
        kind: e.noteKind ?? "general",
        text: e.note?.trim() ?? "",
        weightGrams: e.weightGrams ?? null,
      }))
      // A note says something by its text or, for a bath, by its kind alone.
      .filter((n) => n.text || n.kind !== "general"),
    leftOut,
  };
}
